/**
 * copilotReducer — state machine do CopilotDrawer (TASK-21, ST-02, ST-04).
 *
 * Diagrama de estados (design.md §20.15, Req 9):
 *
 * ```
 * [*] --> closed
 * closed --> open: abrir (atalho ou ação)
 * open --> loading: enviar mensagem
 * loading --> responding: stream de tokens iniciado
 * responding --> open: resposta concluída
 * loading --> error: 4xx ou 5xx recuperável
 * loading --> degraded: 503 provedor indisponível  ← Req 9.4
 * error --> open: nova tentativa do usuário
 * degraded --> open: nova tentativa do usuário     ← ação EXPLÍCITA (sem retry automático)
 * open --> closed: fechar
 * ```
 *
 * Regras de degradação graciosa (Req 9.4, RNF 8):
 * - Apenas a action DEGRADED dispara a transição loading→degraded.
 * - Não existe transição automática que sai de degraded sem ação do usuário (RETRY).
 * - Em degraded, STREAM_START/STREAM_TOKEN/DONE são ignorados.
 */

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

/** Status possíveis do copiloto (state machine design.md §20.15). */
export type CopilotStatus =
  | "closed"
  | "open"
  | "loading"
  | "responding"
  | "error"
  | "degraded";

/** Mensagem do histórico da conversa. */
export interface CopilotMessage {
  readonly id: string;
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly timestamp: number;
}

/** Estado completo do copiloto. */
export interface CopilotState {
  readonly status: CopilotStatus;
  readonly messages: readonly CopilotMessage[];
  /**
   * Buffer de tokens SSE em andamento (role=assistant, parcial).
   * Ao DONE, é consolidado em uma CopilotMessage e zerado.
   */
  readonly streamContent: string;
  readonly errorMessage: string | null;
}

/** Ações da state machine. */
export type CopilotAction =
  | { type: "OPEN" }
  | { type: "CLOSE" }
  /** Envia mensagem do usuário: open → loading. */
  | { type: "SEND"; userMessage: string }
  /** Sinal de início de stream SSE: loading → responding. */
  | { type: "STREAM_START" }
  /** Token SSE incremental: responding → responding (acumula). */
  | { type: "STREAM_TOKEN"; token: string }
  /** Resposta concluída: responding → open. */
  | { type: "DONE" }
  /** Erro recuperável (4xx/5xx genérico): loading/responding → error. */
  | { type: "ERROR"; errorMessage: string }
  /** 503 provedor IA indisponível: loading → degraded (Req 9.4). */
  | { type: "DEGRADED" }
  /** Nova tentativa EXPLÍCITA do usuário: error/degraded → open (Req 9.4). */
  | { type: "RETRY" };

// ---------------------------------------------------------------------------
// Estado inicial
// ---------------------------------------------------------------------------

export const INITIAL_COPILOT_STATE: CopilotState = {
  status: "closed",
  messages: [],
  streamContent: "",
  errorMessage: null,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

/**
 * Reduz o estado do copiloto conforme a state machine do design.md §20.15.
 *
 * Transições inválidas (estado de origem incorreto) retornam o mesmo objeto
 * de estado sem alteração — permite comparação por referência em testes.
 */
export function copilotReducer(
  state: CopilotState,
  action: CopilotAction,
): CopilotState {
  switch (action.type) {
    // ---- closed → open -------------------------------------------------------
    case "OPEN":
      if (state.status === "closed")
        return { ...state, status: "open" };
      return state;

    // ---- open/error/degraded → closed ----------------------------------------
    case "CLOSE":
      if (
        state.status === "open" ||
        state.status === "error" ||
        state.status === "degraded"
      )
        return { ...state, status: "closed", errorMessage: null };
      return state;

    // ---- open → loading (adiciona mensagem do usuário) ------------------------
    case "SEND":
      if (state.status === "open") {
        const userMsg: CopilotMessage = {
          id: crypto.randomUUID(),
          role: "user",
          content: action.userMessage,
          timestamp: Date.now(),
        };
        return {
          ...state,
          status: "loading",
          messages: [...state.messages, userMsg],
          streamContent: "",
          errorMessage: null,
        };
      }
      return state;

    // ---- loading → responding -------------------------------------------------
    case "STREAM_START":
      if (state.status === "loading")
        return { ...state, status: "responding", streamContent: "" };
      return state;

    // ---- responding → responding (acumula token SSE) -------------------------
    case "STREAM_TOKEN":
      if (state.status === "responding")
        return { ...state, streamContent: state.streamContent + action.token };
      return state;

    // ---- responding → open (consolida mensagem do assistente) ----------------
    case "DONE":
      if (state.status === "responding") {
        const assistantMsg: CopilotMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: state.streamContent,
          timestamp: Date.now(),
        };
        return {
          ...state,
          status: "open",
          messages: [...state.messages, assistantMsg],
          streamContent: "",
        };
      }
      return state;

    // ---- loading/responding → error ------------------------------------------
    case "ERROR":
      if (state.status === "loading" || state.status === "responding")
        return { ...state, status: "error", errorMessage: action.errorMessage };
      return state;

    // ---- loading → degraded (Req 9.4: 503 = provedor IA indisponível) --------
    case "DEGRADED":
      if (state.status === "loading")
        return { ...state, status: "degraded", errorMessage: null };
      return state;

    // ---- error/degraded → open (ação EXPLÍCITA do usuário, Req 9.4) ----------
    case "RETRY":
      if (state.status === "error" || state.status === "degraded")
        return { ...state, status: "open", errorMessage: null };
      return state;

    default:
      return state;
  }
}
